import GlobalStylesInjectionPlugin from "@/core/plugins/global-styles-injection";
import PiniaPlugin from "@/core/plugins/pinia";
import PrimeVue from "@/core/plugins/primevue";
import CustomLogger from "@/core/plugins/logger";
import ReactiveAttrsPlugin from "@/core/plugins/reactive-attrs";
import { createApp, h, reactive } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { attrToProp } from "./helpers";
import { i18nSetup } from "./i18n";
import { waitForElement } from "@/common/helpers/js-utils";
import { LogLevels } from "@/core/plugins/logger/types";
export let vueRouter;
const getRootProps = (el) => {
    const rootProps = {};
    try {
        const attributeNames = Object.keys(el.dataset);
        return (attributeNames
            .filter((name) => name != "vApp")
            .reduce((acc, name) => {
            const value = el.dataset[name];
            return { ...acc, ...attrToProp(name, value) };
        }, {}) ?? {});
    }
    catch (e) {
        console.error(e);
    }
    return rootProps;
};
const unmountExistingApp = (el) => {
    const existingVueApp = el?.__vue_app__;
    if (!existingVueApp)
        return;
    existingVueApp.unmount();
    delete el?.__vue_app__;
};
const corePluginsInstallation = async (app, el, rootProps, appOptions) => {
    // Install Vue plugins (except i18n which is installed asynchronously
    if (appOptions?.reactiveAttrs)
        app.use(ReactiveAttrsPlugin, { ...appOptions, el, rootProps });
    app.use(PiniaPlugin, appOptions);
    app.use(PrimeVue);
    app.use(GlobalStylesInjectionPlugin);
    // Install custom logger plugin for Vue 3
    app.use(CustomLogger, {
        logLevel: import.meta.env?.PROD
            ? LogLevels.ERROR // Logging level for production
            : LogLevels.DEBUG // Logging level for development
    });
    // async I18n plugin generation + installation
    const I18nPlugin = await i18nSetup();
    app.use(I18nPlugin);
};
const entryPointConfiguration = (app, appOptions) => {
    let plugins = [...(appOptions?.plugins ?? [])];
    if (appOptions?.routes?.length) {
        window.Ramius.spa = true;
        const router = createRouter({
            history: createWebHistory(),
            routes: typeof appOptions.routes === "function" ? appOptions.routes(app.config.globalProperties.$t) : appOptions.routes,
            scrollBehavior: appOptions.scrollBehavior
        });
        vueRouter = router;
        plugins = [router, ...plugins];
    }
    // Install entry-point-specific plugins
    plugins.forEach(app.use);
    // Register entry-point-specific global components
    Object.entries(appOptions?.globalComponents ?? {}).forEach(([name, component]) => {
        app.component(name, component);
    });
    return app;
};
export const initializeVue = async (elOrId, component, appOptions) => {
    const el = await waitForElement(elOrId);
    if (appOptions?.remountable)
        unmountExistingApp(el);
    // This ensures that rootProps can be reactively updated from outside the Vue app, if needed.
    const rootProps = reactive({
        ...getRootProps(el),
        ...appOptions?.rootProps,
        ...(appOptions?.includeDestroyFn ? { destroyFn: () => unmountExistingApp(el) } : {})
    });
    const app = createApp(() => h(component, rootProps));
    await corePluginsInstallation(app, el, rootProps, appOptions);
    entryPointConfiguration(app, appOptions);
    app.mount(el);
    return app;
};
